import { ServerMessagePayload } from '@common/api';

import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

/**
 * Consolidates walking and idle animation logic.
 *
 * Plays walking animation if:
 * 1. The entity is actually moving (has IsMoving component).
 * 2. OR the entity is the local player and has active movement input (even if blocked).
 *
 * Otherwise, plays directional idle animation.
 */
export class DirectionalAnimationSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['Facing', 'SpritesheetSprite'];

  updateEntity({
    world,
    entityId,
  }: FilteredClientComponentSystemUpdateEntityArgs): ServerMessagePayload[] {
    const facing = world.getComponent(entityId, 'Facing')!;
    const input = world.getComponent(entityId, 'Input');
    const isLocalPlayer = entityId === this.getLocalPlayerEntityId();
    const isMoving = world.hasComponent(entityId, 'IsMoving');
    const isInBed = world.hasComponent(entityId, 'InBed');

    // Check if we should be walking:
    // Actually moving OR (Local Player trying to move)
    // AND NOT in bed
    const isTryingToMove = input && (input.up || input.down || input.left || input.right);
    const shouldWalk = !isInBed && (isMoving || (isLocalPlayer && isTryingToMove));

    let animName: string;

    if (shouldWalk && world.hasComponent(entityId, 'HasWalkingAnimation')) {
      animName = `walk_${facing.direction}`;
    } else if (world.hasComponent(entityId, 'HasIdleAnimation')) {
      animName = `idle_${facing.direction}`;
    } else {
      // No animation markers - nothing to do
      return [];
    }

    let animation = world.getComponent(entityId, 'Animation');

    if (!animation) {
      // Create Animation component
      animation = world.createComponent('Animation', {
        name: animName,
        animationFrame: 1,
        timer: 0,
        loop: true,
        isPlaying: true,
      });
      this.addComponent(entityId, animation);
    } else if (animation.name !== animName) {
      // Animation changed - update and reset timing
      this.updateComponent(entityId, animation, {
        name: animName,
        animationFrame: 1,
        timer: 0,
        currentFrameDurationMs: undefined,
      });
    }

    return [];
  }
}
